import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup
} from "@mui/material";
import Typography from "@mui/material/Typography";
import PhoneNumber from "awesome-phonenumber";
import { pickBy, startCase } from "lodash";
import * as moment from "moment-timezone";
import React, { JSX, useState } from "react";
import { Controller, FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import useCurrentResourceCentreShifts from "../../../hooks/useCurrentResourceCentreShifts";
import {
  convertADtoBS,
  convertBStoAD
} from "../../../components/Calendar/functions/calendarFunctions";
import FormTextField from "../../../components/FormTextField/FormTextField";
import MaterialUiPhoneNumber from "../../../components/PhoneNumber";
import ToggleInputDate from "../../../components/ToggleADBS";
import { t, tl } from "../../../components/translate";
import { isNewPhoneNumber, isStartWith97 } from "../../../helpers/phoneNumber";
import {
  isADDate,
  isBSDate,
  isEmail,
  isISODateString,
  validate
} from "../../../helpers/validators";
import { ChildGeneralLedger, TransactionType } from "../../../interfaces/Accounts";
import { Gender } from "../../../interfaces/ClientInterface";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import { places } from "../../../models/ResourceCentre";
import { RootState } from "../../../store";
import ServiceTagsAutocomplete from "./ServiceTagsAutocomplete";
import Signature from "./Signature";
import SpecialitySearch from "./SpecialitySearch";
import UserGroupSelect from "./UserGroupSelect";
import { UserGroupTypes } from "../../../interfaces/User";
import useGetDepartments from "../../../hooks/department";
import { ResourceCentre } from "../../../interfaces/ResourceCentreInterface";
import ClientImageUploader from "../../../components/ImageUploader/ClientImageUploader";
import { MaritalStatus } from "../../../constants/user";
import { TaxOptedStatus } from "../../../constants/payroll";
import { EmployeeCategory } from "../../../constants/employee";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";

interface SPCreateEditProps {
  spLedger: ChildGeneralLedger;
  isAccountSubscribed: boolean;
  serviceProvider: Partial<ServiceProvider> | undefined;
  onSave: (data: Partial<ServiceProvider>) => void;
  onCancel: () => void;
  createSPMode: boolean;
  resourceCentre: ResourceCentre;
}

export enum UserGroups {
  ServiceProviderFull = "serviceProviderFull",
  ServiceProviderLabTechnician = "serviceProviderLabTechnician",
  Pharmacist = "Pharmacist"
}

export enum DefaultServiceProviders {
  ServiceProviderFull = "serviceProviderFull",
  ServiceProviderLimited = "serviceProviderLimited",
  ServiceProviderLabTechnician = "serviceProviderLabTechnician"
}

export const ALL_DEPARTMENT = "all";

function SPCreateEdit({
  spLedger,
  isAccountSubscribed,
  serviceProvider,
  onSave,
  onCancel,
  createSPMode,
  resourceCentre
}: SPCreateEditProps): JSX.Element {
  const shifts = useCurrentResourceCentreShifts();
  const departments = useGetDepartments({
    showDeletedDepartments: false,
    resourceCentreId: resourceCentre.id
  });
  const resourceCentrePermissionGroups =
    useSelector((state: RootState) => state.resourceCentreUserGroups.collection) || [];

  // const [showOtpInput, setShowOtpInput] = React.useState(false);
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FieldValues>({
    defaultValues: serviceProvider || {
      title: "",
      firstName: "",
      lastName: "",
      gender: "1",
      phone: "",
      address: "",
      city: "",
      otp: "",
      registrationNumber: "",
      specialities: [],
      speciality: "",
      serviceTagIds: [],
      email: "",
      qualification: "",
      details: "",
      tds: "",
      departmentId: ALL_DEPARTMENT,
      designation: "",
      userGroup: "",
      ...(isAccountSubscribed && {
        ledgerId: spLedger ? spLedger.id : "",
        openingBalance: "",
        transactionType: "",
        openingBalanceDate: ""
      }),
      profileImage: "",
      employeeCategory: EmployeeCategory.FULL_TIME,
      panNumber: "",
      dateOfJoining: new Date(),
      dateOfBirth: new Date(),
      maritalStatus: MaritalStatus.SINGLE,
      taxOptedStatus: TaxOptedStatus.SINGLE,
      emergencyContact: {
        contactName: "",
        contactNumber: ""
      },
      bankDetails: {
        name: "",
        address: "",
        accountNumber: "",
        beneficiaryName: ""
      },
      defaultShiftId: null,
      defaultWeeklyOffs: [],
      biometricDeviceCode: ""
    },
    mode: "onChange"
  });
  const [signatureData, setSignatureData] = useState({ hasUpdatedSignature: false, signature: "" });

  const { openingBalanceDate, userGroup, departmentId, profileImage } = watch();

  const options = resourceCentrePermissionGroups
    .filter((group) => group.userGroupType === UserGroupTypes.serviceProvider)
    .filter((group) => (departmentId === ALL_DEPARTMENT ? !group.isDepartment : group.isDepartment))
    .sort((a, b) => a.id - b.id)
    .map((group) => ({
      value: group.name,
      label: startCase(group.name),
      description: group.description || "",
      isDepartmentGroup: group.isDepartment || false
    }));

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const nullableValues = {
      designation: data.designation,
      address: data.address,
      email: data.email,
      speciality: data.speciality,
      departmentId: data.departmentId,
      dateOfJoining: data.dateOfJoining,
      dateOfBirth: data.dateOfBirth,
      defaultShiftId: data.defaultShiftId,
      defaultWeeklyOffs: data.defaultWeeklyOffs,
      biometricDeviceCode: data.biometricDeviceCode
    };

    onSave({ ...pickBy({ ...data, ...signatureData }), ...nullableValues });
  };

  const validateEmail = (value: string) => {
    const { isValid, messages } = validate(value, [isEmail({ msg: "Email address invalid" })]);
    return isValid || messages[0];
  };

  const isValidPhone = (value: string) => {
    const ph = new PhoneNumber(value);
    const phoneNumber = ph.getNumber("significant");
    if (ph.getCountryCode() === 977 && isStartWith97(value)) {
      return isNewPhoneNumber(phoneNumber);
    }
    return ph.isValid() && ph.isMobile();
  };

  const departmentSubscription =
    resourceCentre?.subscriptions.features.department?.subscribed || false;

  const [selectedUserGroupOption, setSelectedUSerGroupOption] = React.useState(null);
  React.useEffect(() => {
    const selectedUserGroup = options.find((option) => option.value === userGroup);
    if (selectedUserGroup) {
      setSelectedUSerGroupOption(selectedUserGroup);
    }
  }, [userGroup]);

  React.useEffect(() => {
    if (serviceProvider) {
      setValue("profileImage", serviceProvider.serviceProviderDetails?.profileImage);
    }
  }, [serviceProvider]);

  // React.useEffect(() => {
  //   (async () => {
  //     if (isValidPhone(phone) && !serviceProvider) {
  //       try {
  //         const hasExistingUser = await Post("/api/users/getOtpToVerifyUser", { phone });
  //         if (hasExistingUser.data?.optSent) {
  //           setShowOtpInput(true);
  //         } else {
  //           setShowOtpInput(false);
  //         }
  //       } catch (error) {
  //         setShowOtpInput(false);
  //       }
  //     }
  //   })();
  // }, [phone]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3
      }}
    >
      <Grid container columnSpacing={2} rowSpacing={2}>
        <Grid size={12}>
          <Box display="flex" justifyContent="center">
            <Controller
              name="profileImage"
              control={control}
              render={({ field }) => (
                <ClientImageUploader
                  action={(image) => {
                    field.onChange(image);
                  }}
                  initialImage={
                    typeof profileImage === "string"
                      ? profileImage
                      : (profileImage && URL.createObjectURL(profileImage)) || ""
                  }
                />
              )}
            />
          </Box>
        </Grid>

        {departmentSubscription && (
          <Grid size={12}>
            <Controller
              name="departmentId"
              control={control}
              render={({ field }) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...field}
                  fullWidth
                  label={t("department")}
                  select
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                  variant="outlined"
                  error={!!errors?.departmentId}
                  onChange={({ target }) => {
                    // case where user selects all then selects specific department and vice versa
                    if (typeof departmentId !== typeof target.value) {
                      setValue("userGroup", "");
                      setSelectedUSerGroupOption(null);
                    }
                    field.onChange(target.value);
                  }}
                >
                  {[
                    ...[{ id: ALL_DEPARTMENT, name: startCase(ALL_DEPARTMENT) }],
                    ...departments
                  ].map((dept) => (
                    <MenuItem value={dept.id} key={dept.id}>
                      {startCase(dept.name)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        )}

        <Grid size={12}>
          <UserGroupSelect
            errors={errors}
            control={control}
            options={options}
            value={userGroup}
            setValue={setValue}
          />
          {selectedUserGroupOption && (
            <Box display="flex" alignItems="center">
              <Typography
                sx={{
                  paddingBottom: "5px",
                  fontSize: "14px"
                }}
              >
                {selectedUserGroupOption?.description || ""}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
      {/* personal details */}
      <Divider>
        <Chip label="Personal Details" size="small" />
      </Divider>
      <Grid container columnSpacing={2} rowSpacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormTextField
            dataTestmation="title"
            placeholder={t("TitleEnter")}
            name="title"
            control={control}
            label={t("Title")}
            errors={errors}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormTextField
            dataTestmation="firstName"
            placeholder={t("EnterFName")}
            name="firstName"
            control={control}
            label={t("FName")}
            errors={errors}
            fullWidth
            rules={{ required: { value: true, message: "First name is required" } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormTextField
            dataTestmation="lastName"
            placeholder={t("EnterLName")}
            name="lastName"
            control={control}
            label={t("LName")}
            errors={errors}
            fullWidth
            rules={{ required: { value: true, message: "Last name is required" } }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <FormTextField
            dataTestmation="registrationNumber"
            placeholder="eg. NMC No: 123 or NHPC No: A01EMDE"
            name="registrationNumber"
            control={control}
            label={t("RegNo.")}
            errors={errors}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormTextField
            dataTestmation="gender"
            placeholder={t("SelectGender")}
            name="gender"
            control={control}
            label={t("Gender")}
            errors={errors}
            fullWidth
            select
            options={[
              { value: Gender.Male, label: "Male" },
              { value: Gender.Female, label: "Female" },
              { value: Gender.Other, label: "Other" }
            ]}
            rules={{ required: { value: true, message: "Gender is required" } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormTextField
            dataTestmation="maritalStatus"
            placeholder="Marital Status"
            name="maritalStatus"
            control={control}
            label="Marital Status"
            errors={errors}
            fullWidth
            select
            options={[
              { value: MaritalStatus.SINGLE, label: "Single" },
              { value: MaritalStatus.MARRIED, label: "Married" }
            ]}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="dateOfJoining"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CalendarDropdown
                date={value ? new Date(value) : new Date()}
                label="Date of Joining"
                fullwidth
                onChange={(v) => onChange(v)}
                TextFieldProps={{
                  margin: "dense",
                  variant: "outlined"
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="dateOfBirth"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CalendarDropdown
                date={value ? new Date(value) : new Date()}
                label="Date of Birth"
                fullwidth
                onChange={(v) => onChange(v)}
                TextFieldProps={{
                  margin: "dense",
                  variant: "outlined"
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="phone"
            control={control}
            rules={{
              required: `${t("MobNo.Req")}`,
              validate: (value) => (value ? isValidPhone(value) : true) || `${t("MobNo.Val")}`
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <MaterialUiPhoneNumber
                data-testmation="phone"
                placeholder="Eg: +9779807100001"
                onChange={onChange}
                value={value}
                onBlur={onBlur}
                fullWidth
                disableAreaCodes
                variant="outlined"
                defaultCountry="np"
                errorMessages={errors?.phone}
                error={!!errors?.phone}
                label={t("MobNo.")}
                helperText={
                  errors?.phone?.message
                  // || `${showOtpInput ? "An Opt has been sent to this number." : ""}`
                }
              />
            )}
          />
        </Grid>
        {/* {showOtpInput && ( */}
        {/*   <Grid size={{ xs: 12, sm: 6 }}> */}
        {/*     <FormTextField */}
        {/*       dataTestmation="otp" */}
        {/*       name="otp" */}
        {/*       control={control} */}
        {/*       label="OTP" */}
        {/*       errors={errors} */}
        {/*       fullWidth */}
        {/*       rules={{ */}
        {/*         // validate: (value) => !value, */}
        {/*         required: { value: true, message: "OTP is required" } */}
        {/*       }} */}
        {/*     /> */}
        {/*   </Grid> */}
        {/* )} */}

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="email"
            name="email"
            control={control}
            label={t("email")}
            errors={errors}
            fullWidth
            placeholder="Eg: email@domain.com"
            rules={{
              validate: (value) => !value || validateEmail(value)
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="address"
            placeholder={t("AddressPlc")}
            name="address"
            control={control}
            label={t("Address")}
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="city"
            control={control}
            render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
              <Autocomplete
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...rest}
                options={places.map((item) =>
                  item.city === item.district ? item.city : `${item.city}, ${item.district}`
                )}
                getOptionLabel={(option) => option}
                onChange={(_, value) => onChange(value)}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                    placeholder={t("CityPlc")}
                    variant="outlined"
                    label={t("City")}
                    margin="normal"
                    error={!!error}
                    helperText={error?.message || null}
                    sx={{ my: 0 }}
                  />
                )}
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <ServiceTagsAutocomplete control={control} name="serviceTagIds" />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          {userGroup === UserGroups.ServiceProviderFull ? (
            <SpecialitySearch control={control} name="specialities" setValue={setValue} />
          ) : (
            <FormTextField
              dataTestmation="speciality"
              placeholder={t("Speciality")}
              name="speciality"
              control={control}
              label={t("Speciality")}
              errors={errors}
              onValueChange={() => {
                setValue("specialities", []);
              }}
              fullWidth
              rules={{
                validate: {
                  required: (value) => {
                    if (userGroup === DefaultServiceProviders.ServiceProviderLimited) {
                      return true;
                    }
                    return !value ? "Speciality is required" : true;
                  }
                }
              }}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="qualification"
            placeholder="Eg: MD, MBBS..."
            name="qualification"
            control={control}
            label={t("Qualification")}
            errors={errors}
            fullWidth
            rules={{ required: { value: true, message: "Qualification is required" } }}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="tds"
            placeholder={t("TDS%Plc")}
            name="tds"
            control={control}
            label={t("TDS%")}
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="employeeCategory"
            placeholder="Employee Category"
            name="employeeCategory"
            control={control}
            label="Employee Category"
            errors={errors}
            fullWidth
            select
            options={[
              { value: EmployeeCategory.FULL_TIME, label: "Full Time" },
              { value: EmployeeCategory.PART_TIME, label: "Part Time" },
              { value: EmployeeCategory.INTERN, label: "Intern" }
            ]}
          />
        </Grid>

        <Grid size={6}>
          <FormTextField
            dataTestmation="panNumber"
            placeholder={t("panNumber")}
            name="panNumber"
            control={control}
            label={t("panNumber")}
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid size={12}>
          <FormTextField
            multiline
            dataTestmation="bio"
            placeholder={t("DetailsPlc")}
            name="details"
            control={control}
            label={t("Details")}
            errors={errors}
            fullWidth
          />
        </Grid>
      </Grid>
      {/* payroll details */}
      <Divider>
        <Chip label="Payroll Specific Details" size="small" />
      </Divider>
      <Grid size={12} container columnSpacing={2} rowSpacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="taxOptedStatus"
            placeholder="Tax Opted Status"
            name="taxOptedStatus"
            control={control}
            label="Tax Opted Status"
            errors={errors}
            fullWidth
            select
            options={[
              { value: TaxOptedStatus.SINGLE, label: "Single" },
              { value: TaxOptedStatus.COUPLE, label: "Married" }
            ]}
          />
        </Grid>
      </Grid>
      {/* emergency datails */}
      <Divider>
        <Chip label="Emergency Contact Details" size="small" />
      </Divider>
      <Grid size={12} container columnSpacing={2} rowSpacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="emergencyContactName"
            placeholder="Emergency Contact Name"
            name="emergencyContact.contactName"
            control={control}
            label="Contact Name"
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid size={6}>
          <Controller
            name="emergencyContact.contactNumber"
            control={control}
            rules={{
              validate: (value) => (value ? isValidPhone(value) : true) || `${t("MobNo.Val")}`
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <MaterialUiPhoneNumber
                data-testmation="emergencyContactNumber"
                placeholder="Eg: +9779807100001"
                onChange={onChange}
                value={value}
                onBlur={onBlur}
                fullWidth
                disableAreaCodes
                variant="outlined"
                defaultCountry="np"
                errorMessages={errors?.emergencyContactNumber || null}
                error={!!errors?.emergencyContactNumber}
                label="Emergency Contact Number"
                helperText={errors?.emergencyContactNumber?.message}
              />
            )}
          />
        </Grid>
      </Grid>
      <Divider>
        <Chip label="Bank Details" size="small" />
      </Divider>
      <Grid size={12} container columnSpacing={2} rowSpacing={2}>
        <Grid size={6}>
          <FormTextField
            dataTestmation="bankName"
            placeholder="Bank Name"
            name="bankDetails.name"
            control={control}
            label="Bank Name"
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid size={6}>
          <FormTextField
            dataTestmation="accountNumber"
            placeholder="Account Number"
            name="bankDetails.accountNumber"
            control={control}
            label="Account Number"
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid size={6}>
          <FormTextField
            dataTestmation="bankAddress"
            placeholder="Bank Address"
            name="bankDetails.address"
            control={control}
            label="Bank Address"
            errors={errors}
            fullWidth
          />
        </Grid>

        <Grid size={6}>
          <FormTextField
            dataTestmation="beneficiaryName"
            placeholder="Beneficiary Name"
            name="bankDetails.beneficiaryName"
            control={control}
            label="Beneficiary Name"
            errors={errors}
            fullWidth
          />
        </Grid>
      </Grid>
      {/* shift settings */}
      <Divider>
        <Chip label="Shift Settings" size="small" />
      </Divider>
      <Grid container columnSpacing={2} rowSpacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="defaultShiftSelect"
            placeholder="Default Shift"
            name="defaultShiftId"
            control={control}
            label="Default Shift"
            errors={errors}
            fullWidth
            select
            options={shifts.map((shift) => ({ value: shift.id, label: shift.name }))}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            dataTestmation="biometricDeviceCode"
            placeholder="Biometric Code"
            name="biometricDeviceCode"
            control={control}
            label="Biometric Code"
            errors={errors}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Weekly Offs
          </Typography>
          <FormGroup row>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => (
              <Controller
                key={day}
                name="defaultWeeklyOffs"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={field.value?.includes(index)}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? [...(field.value || []), index]
                            : field.value?.filter((v: number) => v !== index);
                          field.onChange(newValue);
                        }}
                      />
                    }
                    label={<Typography variant="body2">{day}</Typography>}
                  />
                )}
              />
            ))}
          </FormGroup>
        </Grid>
      </Grid>
      {isAccountSubscribed && (
        <>
          <Divider>
            <Chip label="Account Details" size="small" />
          </Divider>
          <Grid container columnSpacing={2} rowSpacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextField
                dataTestmation="transactionType"
                placeholder={t("transactionTypePlc")}
                name="transactionType"
                control={control}
                label={t("transactionType")}
                errors={errors}
                fullWidth
                select
                options={[
                  { value: TransactionType.DEBIT, label: startCase(TransactionType.DEBIT) },
                  { value: TransactionType.CREDIT, label: startCase(TransactionType.CREDIT) }
                ]}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormTextField
                dataTestmation="openingBalance"
                placeholder={t("OpeningBalancePlc")}
                name="openingBalance"
                control={control}
                label={t("OpeningBalance")}
                errors={errors}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} mt={-1}>
              <Controller
                name="openingBalanceDate"
                control={control}
                rules={{
                  validate: (value) =>
                    (value ? !!isISODateString(value) : true) || "Please enter valid date"
                }}
                render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
                  <ToggleInputDate
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...rest}
                    key="openingBalanceDate"
                    field={{
                      value: openingBalanceDate,
                      key: "openingBalanceDate",
                      label: `${t("OpeningBalanceDate")}`,
                      inputType: "bsDate",
                      editable: true,
                      creatable: true,
                      required: false,
                      showAgeField: false,
                      formatter: (
                        dateString: Date | moment.Moment | string
                      ): Date | moment.Moment | string => {
                        if (!dateString) return dateString;
                        const isValid = moment(dateString).toISOString() === dateString;
                        if (!isValid) return dateString;
                        return convertADtoBS(moment.utc(dateString)).formatted4;
                      },
                      reverseFormatter: (
                        dateString: Date | moment.Moment | string
                      ): Date | moment.Moment | string | null => {
                        if (!dateString) return null;
                        const isValid = isBSDate({ msg: "" })(dateString);
                        if (!dateString || !isValid) return dateString;
                        const [year, month, date] = dateString.split("-");
                        return moment
                          .utc(
                            // when we have a date string without timezone, it is better to force them at +0 UTC
                            moment(convertBStoAD(Number(year), Number(month), Number(date))).format(
                              "YYYYMMDD"
                            )
                          )
                          .toISOString();
                      },
                      ADFormatter: (
                        dateString: Date | moment.Moment | string
                      ): Date | moment.Moment | string | null => {
                        if (!dateString) return null;
                        const isValid = isADDate({ msg: "" })(dateString);
                        if (!dateString || !isValid) return dateString;
                        return moment.utc(dateString).toISOString();
                      }
                    }}
                    data={{ openingBalanceDate }}
                    changeDate={(value) => onChange(value)}
                    showAgeField={false}
                    error={!!error}
                    helperText={error?.message || null}
                  />
                )}
              />
            </Grid>
            {spLedger && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="ledger"
                  placeholder={t("ledger")}
                  name="ledgerId"
                  control={control}
                  label={t("ledger")}
                  errors={errors}
                  rules={{ required: { value: true, message: "Ledger is required" } }}
                  fullWidth
                  select
                  options={[{ value: spLedger.id, label: spLedger.ledgerName }]}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}
      <Divider>
        <Chip label="Signature Details" size="small" />
      </Divider>{" "}
      <Grid container columnSpacing={2} rowSpacing={2} mt={1}>
        <Grid size={12}>
          <Signature signature={serviceProvider?.signature} setSignatureData={setSignatureData} />
        </Grid>
        <Grid size={12}>
          <Typography sx={{ fontWeight: 600 }}>{tl("signature.designation")} </Typography>
          <Typography sx={{ textDecoration: "underline", fontSize: "0.75rem", mb: 2 }}>
            {tl("signature.designationNote")}
          </Typography>
          <FormTextField
            dataTestmation="designation"
            placeholder={t("signature.designation")}
            name="designation"
            control={control}
            label={t("Designation")}
            errors={errors}
            multiline
          />
        </Grid>
      </Grid>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          left: 0,
          px: 4,
          py: 1,
          gap: 4,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end"
        }}
      >
        <Button onClick={onCancel}>{tl("cancel")}</Button>
        <Button
          data-testmation="spCreate"
          color="primary"
          variant="contained"
          type="submit"
          onClick={handleSubmit(onSubmit)}
        >
          {createSPMode ? tl("serviceProvider.create") : tl("serviceProvider.save")}
        </Button>
      </Box>
    </Box>
  );
}

export default SPCreateEdit;
